import React, { useEffect, useMemo } from "react";
import { connect } from 'react-redux';
import CreatableSelect from "react-select/async-creatable";
import Tooltip from "@/tooltip";
import { Actions as QUICKBOOKS_ACTIONS } from './store/quickbooksActions';
import { Actions as INTEGRATION_ACTIONS } from '../../../store/actions/integrationActions';
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import pDebounce from "p-debounce";

const QuickbooksEmployeeLink = ({
    agreement,
    employeeDetails,
    thirdPartyList,
    getQuickbooksContacts,
    thirdPartyContact,
    creatingVendor,
    updateVendorList,
    getQuickbooksEmployees,
    desyncingVendor,
    createQuickbooksVendor,
    disabled,
    loadingEmployees,
    switchEmployeeLink,
    label,
    selectedValue,
    onChange,
}) => {
    const { givenName, familyName } = employeeDetails || {}

    useEffect(() => {
        if (!thirdPartyContact) getQuickbooksEmployees()
        return
    }, [])

    const vendorOptionsPromise = pDebounce((inputValue) => {
        return new Promise(async resolve => {
            const contacts = await getQuickbooksContacts(inputValue)
            updateVendorList(contacts)
            resolve(contacts)
        })
    }, 300)

    const handleCreate = (inputValue) => {
        const data = {}

        if (!agreement) data.name = inputValue
        else data.agreement = agreement

        createQuickbooksVendor(data)
    }

    const placeholder = useMemo(() => {
        if (desyncingVendor) return 'Desyncing Vendor...'
        if (loadingEmployees) return 'Loading Vendor...'
        if (creatingVendor) return 'Creating Vendor...'

        return <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>Search or Create</div>
            <MagnifyingGlassIcon style={{ height: '2rem', display: 'block', paddingRight: '2rem' }} />
        </div>
    }, [loadingEmployees, creatingVendor])

    return (
        <>
            <div className={`col-xs-${agreement ? '3' : '12'}`}>
                <div className={agreement ? 'form-material form-container' : null}>
                    <label htmlFor="material-text">
                        <Tooltip content="The vendor is used when issuing bills to the company">
                            {label ? label : 'QuickBooks Link'}
                        </Tooltip>
                    </label>
                    <CreatableSelect
                        isDisabled={disabled || creatingVendor || loadingEmployees}
                        allowCreateWhileLoading={false}
                        formatCreateLabel={(inputValue) => `Create "${agreement ? `${givenName} ${familyName}` : inputValue}"`}
                        name="quickbooks-thirdPartyLink"
                        classNamePrefix="react-select-search"
                        createOptionPosition="last"
                        isClearable
                        isLoading={creatingVendor || loadingEmployees}
                        onCreateOption={handleCreate}
                        defaultOptions={thirdPartyList}
                        value={selectedValue ? selectedValue : thirdPartyContact}
                        onChange={(selected) => {
                            if (agreement) switchEmployeeLink(selected?.value || 'desynced')
                            else onChange(selected)
                        }}
                        loadOptions={vendorOptionsPromise}
                        placeholder={placeholder}
                    />
                </div>
            </div>
        </>
    )
}


const mapStateToProps = (state, ownProps) => {
    const agreementInfo = ownProps.agreementInfo ? ownProps.agreementInfo : state.employee.agreementInfo;
    const { id: agreement, employeeDetails } = agreementInfo || {}

    const { quickbooks } = state?.integration?.accounting || {}

    const { desyncingVendor, creatingVendor, loadingEmployees, links, vendorList: thirdPartyList } = quickbooks || {}

    const thirdPartyId = state.employee?.quickbooksLink
    const existingLink = links?.find(e => e?.agreement == agreementInfo?.id)
    const currentLink = thirdPartyList?.find?.(e => e?.link == thirdPartyId)
    const thirdPartyContact = (()=> {
        if (thirdPartyId) return currentLink
        if (!desyncingVendor) return existingLink
    })()

    return {
        agreement,
        employeeDetails,
        creatingVendor,
        desyncingVendor,
        loadingEmployees,
        thirdPartyList,
        thirdPartyContact,
    }
}

const mapDispatchToProps = dispatch => ({
    getQuickbooksContacts: (query) => QUICKBOOKS_ACTIONS.getQuickbooksContacts({ query }),
    getQuickbooksEmployees: () => dispatch(QUICKBOOKS_ACTIONS.getQuickbooksEmployees()),
    createQuickbooksVendor: ({ agreement, name }) => dispatch(QUICKBOOKS_ACTIONS.createQuickbooksVendor({ agreement, name })),
    updateVendorList: (vendorList) => dispatch(QUICKBOOKS_ACTIONS.updateVendorList(vendorList)),
    switchEmployeeLink: (employee) => dispatch(INTEGRATION_ACTIONS.switchEmployeeLink(employee, 'quickbooks')),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(QuickbooksEmployeeLink)